import { createFileRoute, Outlet } from '@tanstack/react-router';
import PageTabs, { type TabEntry } from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';

export const Route = createFileRoute(
  '/management/risk-management/(pages)/(list)',
)({
  component: RiskManagementTabs,
});

/**
 * The tab row, shared by the three lists beneath it.
 *
 * Each tab is its own route rather than a `?tab=` on one: they are three
 * unrelated tables with their own columns, filters, paging and prefetch, and a
 * single route meant one loader branching three ways and one search schema
 * serving all of them.
 *
 * The rule editor sits outside this layout — it is a page, not a tab.
 */
function RiskManagementTabs() {
  const { getTranslation: t } = useTranslator({
    page: 'managementRiskManagement',
  });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });

  const tabs: TabEntry[] = [
    {
      key: 'risk-gross',
      label: t('tabs.riskGross'),
      to: '/management/risk-management/risk-gross',
    },
    {
      key: 'risks',
      label: t('tabs.risks'),
      to: '/management/risk-management/risks',
    },
    {
      key: 'rule-templates',
      label: t('tabs.ruleTemplates'),
      to: '/management/risk-management/rule-templates',
    },
  ];

  // Tabs sit outside `PageContainer`: the container is the table's
  // viewport-filling frame, and a tab row inside it would scroll with the rows.
  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      <PageTabs tabs={tabs} mode='path' moreLabel={tShared('moreTabs')} />
      <Outlet />
    </div>
  );
}
